import React, { useMemo, useState } from 'react';

import styles from './LiveUpdatesPage.module.css';
import MarketingShell from '../../marketing/MarketingShell';
import theme from '../../marketing/theme.module.css';

const jurisdictionFilters = ['All', 'Federal', 'State', 'Local'] as const;

type JurisdictionFilter = (typeof jurisdictionFilters)[number];

type LiveEvent = {
  id: string;
  title: string;
  meta: string;
  status: string;
  jurisdiction: JurisdictionFilter;
};

const liveEvents: LiveEvent[] = [
  {
    id: 'house-ai',
    title: 'House Subcommittee on AI',
    meta: 'Impact score 9.4',
    status: 'Streaming live',
    jurisdiction: 'Federal',
  },
  {
    id: 'california-sb',
    title: 'California SB-1042',
    meta: 'Summary ready',
    status: 'Awaiting vote',
    jurisdiction: 'State',
  },
  {
    id: 'nyc-local-law',
    title: 'NYC Local Law 144',
    meta: 'Awaiting committee',
    status: 'Under review',
    jurisdiction: 'Local',
  },
  {
    id: 'washington-cc',
    title: 'Washington City Council AI Briefing',
    meta: 'Notes being drafted',
    status: 'In progress',
    jurisdiction: 'Local',
  },
  {
    id: 'oregon-energy',
    title: 'Oregon Energy Committee',
    meta: 'Impact score 8.1',
    status: 'Hearing scheduled tomorrow',
    jurisdiction: 'State',
  },
  {
    id: 'senate-ai',
    title: 'US Senate AI Task Force',
    meta: 'Summary in review',
    status: 'Briefing ready',
    jurisdiction: 'Federal',
  },
];

export const LiveUpdatesPage: React.FC = () => {
  const [filter, setFilter] = useState<JurisdictionFilter>('All');

  const filteredEvents = useMemo(() => {
    if (filter === 'All') return liveEvents;
    return liveEvents.filter((event) => event.jurisdiction === filter);
  }, [filter]);

  const totalStreaming = liveEvents.filter((event) => event.status.toLowerCase().includes('live')).length;
  const totalJurisdictions = new Set(liveEvents.map((event) => event.jurisdiction)).size;

  return (
    <MarketingShell>
      <div className={styles.page}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          {/* The theme's own eyebrow and heading rather than the application's
              PageHeading, whose accent is an italic serif — a different voice
              from KN's, which accents by colouring the word at the same
              weight. Every other public page does the same. */}
          <p className={theme.eyebrow}>
            <span className={theme.eyebrowDash} aria-hidden="true" />
            Live feed
          </p>
          <h1 className={theme.h2}>
            Live <span className={theme.accentOrange}>Updates</span> Feed
          </h1>
          <p className={styles.description}>
            Explore every hearing, bill, and regulatory action Policyora is tracking right now. Filter by jurisdiction to zero in on
            the regions and agencies that matter most to your team.
          </p>
        </div>

        <div className={styles.controls}>
          <div className={styles.filterGroup}>
            {jurisdictionFilters.map((option) => (
              <button
                key={option}
                type="button"
                className={`${styles.filterButton} ${filter === option ? styles.activeFilter : ''}`}
                onClick={() => setFilter(option)}
              >
                {option}
              </button>
            ))}
          </div>
          <span className={styles.timestamp}>Last refreshed · a few seconds ago</span>
        </div>

        <div className={styles.stats}>
          <div className={styles.statCard}>
            <span>Streaming now</span>
            <strong>{totalStreaming}</strong>
          </div>
          <div className={styles.statCard}>
            <span>Total jurisdictions</span>
            <strong>{totalJurisdictions}</strong>
          </div>
          <div className={styles.statCard}>
            <span>Total events</span>
            <strong>{liveEvents.length}</strong>
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className={styles.empty}>
            Nothing live for this jurisdiction yet. Try another filter or check back soon!
          </div>
        ) : (
          <div className={styles.grid}>
            {filteredEvents.map((event) => (
              <article key={event.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <p className={styles.cardTitle}>{event.title}</p>
                  <span className={styles.chip}>{event.jurisdiction}</span>
                </div>
                <p className={styles.cardMeta}>{event.meta}</p>
                <p className={styles.cardStatus}>{event.status}</p>
              </article>
            ))}
          </div>
        )}
      </div>
      </div>
    </MarketingShell>
  );
};

export default LiveUpdatesPage;
